import { bus, EVENTS } from '../lib/eventBus';
import { classifyGesture } from '../lib/gestureHeuristics';
import { classifyBimanual } from '../lib/gestureBimanual';
import {
  classifyTemporal,
  isInMotion,
  hasNoticeableMotion,
  MOTION_THRESHOLDS,
} from '../lib/gestureTemporal';

const DEBOUNCE_MS = 500;
const COOLDOWN_MS = 1500;
const TEMPORAL_BUFFER_MAX = 30; // ~1s @ 30fps
const TEMPORAL_TICK_MS = 300;

/**
 * ContextAgent — Phase 5 (dual-hand aware).
 *
 * Pipelines:
 *   1. Static single-hand — per-frame, runs allowedStaticIds heuristics against
 *      EACH hand in frame; picks the highest-confidence match. Suppressed when
 *      any hand is in motion (avoids Hello firing during a wave).
 *   2. Bimanual static — runs only when 2 hands are present, against
 *      allowedBimanualIds (Book/More/House). Returns a single best match;
 *      competes with single-hand on confidence.
 *   3. Temporal — every 300 ms over a per-hand 30-frame buffer. Each hand's
 *      buffer is scored independently.
 *
 * Catalog-only entries are never scored. Bimanual entries require
 * requires_two_hands === true on the dictionary entry.
 */
export class ContextAgent {
  constructor() {
    this.entries = [];
    this.category = 'all';
    this.allowedStaticIds = new Set();
    this.allowedTemporalIds = new Set();
    this.allowedBimanualIds = new Set();

    this.candidateBuffer = [];                  // [{id, ts}] — debounce
    this.landmarkBuffers = [[], []];            // per-hand temporal windows
    this.lastConfirmedAt = new Map();
    this.lastEmittedHandStatus = null;

    this.unsubscribe = bus.on(EVENTS.LANDMARKS_DETECTED, (p) => this._onLandmarks(p));
    this.temporalTimer = setInterval(() => this._temporalTick(), TEMPORAL_TICK_MS);
  }

  setDictionary(entries) {
    this.entries = Array.isArray(entries) ? entries : [];
    this._rebuildAllowed();
  }

  setCategory(category) {
    this.category = category || 'all';
    this._rebuildAllowed();
  }

  _rebuildAllowed() {
    const enabled = this.entries.filter((e) => {
      if (e.detection_status === 'catalog_only') return false;
      const cats = e.categories || ['everyday_basics'];
      if (this.category && this.category !== 'all') return cats.includes(this.category);
      return true;
    });

    // Static single-hand: detection_status='static' AND NOT requires_two_hands
    this.allowedStaticIds = new Set(
      enabled
        .filter((e) => e.detection_status === 'static' && !e.requires_two_hands)
        .map((e) => e.id),
    );
    // Bimanual: detection_status='static' AND requires_two_hands === true
    this.allowedBimanualIds = new Set(
      enabled
        .filter((e) => e.detection_status === 'static' && e.requires_two_hands === true)
        .map((e) => e.id),
    );
    // Temporal: detection_status='temporal' (current temporal scorers are all single-hand)
    this.allowedTemporalIds = new Set(
      enabled.filter((e) => e.detection_status === 'temporal').map((e) => e.id),
    );

    this.candidateBuffer = [];
    this.landmarkBuffers = [[], []];
    this.lastEmittedHandStatus = null;
  }

  _findEntry(id) {
    return this.entries.find((e) => e.id === id);
  }

  _findWord(id) {
    return this._findEntry(id)?.word || id;
  }

  _emitStatus(status, extra = {}) {
    if (status === 'motion-detecting' && this.lastEmittedHandStatus === 'motion-detecting') return;
    this.lastEmittedHandStatus = status;
    bus.emit(EVENTS.HAND_STATUS, { status, ...extra });
  }

  _onLandmarks(payload) {
    // Backwards-compat: old payload shape was { landmarks, timestamp }. Phase 5
    // sends { hands: [...], timestamp }. Coerce safely.
    const timestamp = payload?.timestamp ?? performance.now();
    const hands = Array.isArray(payload?.hands)
      ? payload.hands
      : payload?.landmarks
        ? [{ landmarks: payload.landmarks, handedness: 'Right', score: 1 }]
        : [];

    if (hands.length === 0) {
      if (this.candidateBuffer.length) {
        this.candidateBuffer = [];
        bus.emit(EVENTS.GESTURE_CANDIDATE, null);
      }
      // Decay both buffers so motion-detection doesn't latch on stale frames
      this.landmarkBuffers.forEach((buf) => { if (buf.length) buf.shift(); });
      return;
    }

    // 1. Push per-hand frames into per-hand temporal buffers
    for (let i = 0; i < this.landmarkBuffers.length; i++) {
      if (i < hands.length) {
        this.landmarkBuffers[i].push({ lm: hands[i].landmarks, ts: timestamp });
        if (this.landmarkBuffers[i].length > TEMPORAL_BUFFER_MAX) this.landmarkBuffers[i].shift();
      } else if (this.landmarkBuffers[i].length) {
        // Hand i not in frame → decay its buffer
        this.landmarkBuffers[i].shift();
      }
    }

    // 2. Motion suppression: any moving hand suppresses static classification.
    //    Necessary to prevent Hello firing mid-Goodbye-wave.
    const anyInMotion = this.landmarkBuffers.some(
      (buf) => buf.length >= MOTION_THRESHOLDS.MIN_BUFFER_FRAMES && isInMotion(buf),
    );
    if (anyInMotion) {
      if (this.candidateBuffer.length) {
        this.candidateBuffer = [];
        bus.emit(EVENTS.GESTURE_CANDIDATE, null);
      }
      return;
    }

    // 3. Classify. Single-hand scorers run per hand; bimanual only if 2 hands.
    let best = null;
    for (const h of hands) {
      const r = classifyGesture(h.landmarks, this.allowedStaticIds);
      if (r && (!best || r.confidence > best.confidence)) best = r;
    }
    if (hands.length >= 2 && this.allowedBimanualIds.size > 0) {
      const r = classifyBimanual(hands, this.allowedBimanualIds);
      if (r && (!best || r.confidence > best.confidence)) best = r;
    }

    bus.emit(EVENTS.GESTURE_CANDIDATE, best);

    if (!best) {
      this.candidateBuffer = [];
      return;
    }

    if (this.candidateBuffer.length && this.candidateBuffer[0].id !== best.id) {
      this.candidateBuffer = [];
    }
    this.candidateBuffer.push({ id: best.id, ts: timestamp });

    const stableDuration = timestamp - this.candidateBuffer[0].ts;
    if (stableDuration < DEBOUNCE_MS) return;

    const lastTs = this.lastConfirmedAt.get(best.id) || 0;
    if (timestamp - lastTs < COOLDOWN_MS) return;

    this._confirm(best.id, timestamp);
  }

  _temporalTick() {
    if (this.allowedTemporalIds.size === 0) {
      // Still emit motion chip if there's wrist motion (e.g. waiting category)
    }

    // Evaluate each hand's buffer independently — the first to hit threshold wins.
    for (let i = 0; i < this.landmarkBuffers.length; i++) {
      const buf = this.landmarkBuffers[i];
      if (buf.length < MOTION_THRESHOLDS.MIN_BUFFER_FRAMES) continue;
      if (this.allowedTemporalIds.size === 0) continue;

      const result = classifyTemporal(buf, this.allowedTemporalIds);
      if (result) {
        const now = performance.now();
        const lastTs = this.lastConfirmedAt.get(result.id) || 0;
        if (now - lastTs >= COOLDOWN_MS) {
          this._confirm(result.id, now);
          // Clear ALL temporal buffers to avoid cross-hand double confirmation.
          this.landmarkBuffers = [[], []];
          return;
        }
      }
    }

    // No confirmation — surface "motion-detecting" feedback if any hand is moving.
    const anyMotion = this.landmarkBuffers.some(
      (buf) => buf.length >= 15 && hasNoticeableMotion(buf),
    );
    if (anyMotion) this._emitStatus('motion-detecting');
  }

  _confirm(id, timestamp) {
    this.lastConfirmedAt.set(id, timestamp);
    this.candidateBuffer = [];
    this.lastEmittedHandStatus = 'recognized';
    const word = this._findWord(id);
    bus.emit(EVENTS.GESTURE_CONFIRMED, { gesture: id, word });
    bus.emit(EVENTS.HAND_STATUS, { status: 'recognized', gesture: id, word });
    bus.emit(EVENTS.PHRASE_READY, { text: word, gesture: id });
  }

  destroy() {
    this.unsubscribe?.();
    if (this.temporalTimer) clearInterval(this.temporalTimer);
    this.candidateBuffer = [];
    this.landmarkBuffers = [[], []];
    this.lastConfirmedAt.clear();
    this.lastEmittedHandStatus = null;
  }
}
